// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { useLocalMessage } from '@Helpers/';
import { Form, FormInstance } from 'antd';
import { THForm } from '@Molecules/THForm/THForm';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THRadio } from '@Molecules/Inputs/THRadio/THRadio';

import './ExportForm.scss';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { ICONS } from '@Constants/iconsApp';

export interface ExportValuesForm {
    format?: 'csv' | 'json';
    delimiter?: ',' | ';' | '\t' | '\\t' | ' ';
}

interface ExportFormProps {
    onFinish(values: ExportValuesForm): void;
    form: FormInstance;
    isValid: boolean;
    onChange: () => void;
    onCancel: () => void;
    number: number;
}

export const ExportForm = ({ onFinish, form, isValid, onChange, onCancel, number }: ExportFormProps): ReactElement => {
    const i = useLocalMessage();

    const onChangeHandler = () => {
        onChange();
    };

    const format = Form.useWatch<ExportValuesForm['format']>(['format'], form);
    const fileFormatLabel = (
        <span className="fileFormatLabel">
            {i('export.drawer.form.file-format.label')}
            <THTooltip title={i('export.drawer.file-format-tooltip')} placement={'right'}>
                <ICONS.QuestionCircleOutlined />
            </THTooltip>
        </span>
    );

    return (
        <THForm<ExportValuesForm>
            name="export-values-form"
            onFinish={onFinish}
            form={form}
            onValuesChange={onChangeHandler}
        >
            <THForm.Layout
                footer={
                    <FormSubmitButton
                        onCancel={onCancel}
                        disabled={!isValid}
                        footerText={i(`export.drawer.export-to-file`)}
                    />
                }
            >
                <div className={'info-number'}>
                    <span>{i(`export.drawer.info-number.${number > 1 ? 'multiple' : 'single'}`, { number })}</span>
                </div>
                <THForm.Item
                    label={fileFormatLabel}
                    name="format"
                    rules={[{ required: true, message: i('export.drawer.form.file-format.required') }]}
                >
                    <THRadio.Group>
                        <THRadio.Button value="csv">{i('export.drawer.form.file-format.csv')}</THRadio.Button>
                        <THRadio.Button value="json">{i('export.drawer.form.file-format.json')}</THRadio.Button>
                    </THRadio.Group>
                </THForm.Item>
                <THForm.Item
                    label={i('export.drawer.form.delimiter.label')}
                    name="delimiter"
                    rules={[{ required: format === 'csv', message: i('export.drawer.form.delimiter.required') }]}
                    hidden={format !== 'csv'}
                >
                    <THRadio.Group>
                        <THRadio.Button value="\t">{i('export.drawer.form.delimiter.tab')}</THRadio.Button>
                        <THRadio.Button value=";">{i('export.drawer.form.delimiter.semicolon')}</THRadio.Button>
                        <THRadio.Button value=",">{i('export.drawer.form.delimiter.comma')}</THRadio.Button>
                    </THRadio.Group>
                </THForm.Item>
            </THForm.Layout>
        </THForm>
    );
};
